import { GraphUtils } from '@helpers/graph.helper';
import { Sensor } from '@models/sensor/sensor';
import { Router } from '@angular/router';
import { UtilsHelper } from '@helpers/utils.helper';

export class SoilTemperatureAttribute extends Sensor {
  public soilTemperatureChart: any;
  public soilTemperatureSensor: any[][][] = []; // [position][probeIndex][dataPoints]
  public iconsToDisplay: { icon: string, type: number }[] = [];

  constructor(router: Router, url: string, alertsActive: boolean) {
    super(router, url, alertsActive);
    this.soilTemperatureChart = null;
  }

  private ensureArraySize(arr: any[][][], position: number): void {
    while (arr.length <= position) {
      arr.push([[], [], [], [], [], [], [], []]); // Hasta 8 sondas: soilTemperature1 ... soilTemperature8
    }
  }

  prepareData(): void {
    const iconMap = new Map<string, { icon: string, type: number }>();

    for (const element of this.data) {
      const date = new Date(element.date);
      const timestamp = GraphUtils.getDateUtc(date);
      const position = element.position ?? 0;

      const sensorInfo = UtilsHelper.nameSensor(element.type);
      const icon = sensorInfo.icon;

      if (!iconMap.has(icon)) {
        iconMap.set(icon, {
          icon,
          type: element.type
        });
      }

      this.ensureArraySize(this.soilTemperatureSensor, position);

      if (element.soilTemperature1 !== undefined) {
        this.soilTemperatureSensor[position][0].push([timestamp, element.soilTemperature1]);
      }
      if (element.soilTemperature2 !== undefined) {
        this.soilTemperatureSensor[position][1].push([timestamp, element.soilTemperature2]);
      }
      if (element.soilTemperature3 !== undefined) {
        this.soilTemperatureSensor[position][2].push([timestamp, element.soilTemperature3]);
      }
      if (element.soilTemperature4 !== undefined) {
        this.soilTemperatureSensor[position][3].push([timestamp, element.soilTemperature4]);
      }
      if (element.soilTemperature5 !== undefined) {
        this.soilTemperatureSensor[position][4].push([timestamp, element.soilTemperature5]);
      }
      if (element.soilTemperature6 !== undefined) {
        this.soilTemperatureSensor[position][5].push([timestamp, element.soilTemperature6]);
      }
      if (element.soilTemperature7 !== undefined) {
        this.soilTemperatureSensor[position][6].push([timestamp, element.soilTemperature7]);
      }
      if (element.soilTemperature8 !== undefined) {
        this.soilTemperatureSensor[position][7].push([timestamp, element.soilTemperature8]);
      }
    }
    this.iconsToDisplay = Array.from(iconMap.values());

  }

  public setCharts(): void {
    const temperatureSeries: any[] = [];

    this.soilTemperatureSensor.forEach((probes, position) => {
      probes.forEach((data, probeIndex) => {
        if (data.length > 0) {
          const hasOnlySoilTemperatureField = probes.filter(p => p.length > 0).length === 1;
          const name = hasOnlySoilTemperatureField
            ? `S${position + 1}: Temp`
            : `S${position + 1}: Temp ${probeIndex + 1}`;

          temperatureSeries.push({
            name,
            data
          });
        }
      });
    });

    this.soilTemperatureChart = this.getConfigChar(
      'soilTemperature',
      temperatureSeries,
      this.getRanges('soilTemperature'),
      this.getLimits('soilTemperature')
    );
  }

  private decodePosition(pos: number): [number, number] {
    const position = Math.floor((pos - 1) / 8);
    const probeIndex = (pos - 1) % 8;
    return [position, probeIndex];
  }

  public hasSoilTemperatureSensor(pos: number): boolean {
    const [position, probeIndex] = this.decodePosition(pos);
    return this.soilTemperatureSensor[position]?.[probeIndex]?.length > 0;
  }

  public getSoilTemperatureSensor(pos: number): any {
    const [position, probeIndex] = this.decodePosition(pos);
    const data = this.soilTemperatureSensor[position]?.[probeIndex];
    return data?.length > 0 ? data[data.length - 1][1] ?? null : null;
  }

  public static getVariables(): any[] {
    return [
      { title: 'Temperatura del Suelo', value: 'soilTemperature' },
      { title: 'Temperatura del Suelo 1', value: 'soilTemperature1' },
      { title: 'Temperatura del Suelo 2', value: 'soilTemperature2' },
      { title: 'Temperatura del Suelo 3', value: 'soilTemperature3' },
      { title: 'Temperatura del Suelo 4', value: 'soilTemperature4' },
      { title: 'Temperatura del Suelo 5', value: 'soilTemperature5' },
      { title: 'Temperatura del Suelo 6', value: 'soilTemperature6' },
      { title: 'Temperatura del Suelo 7', value: 'soilTemperature7' },
      { title: 'Temperatura del Suelo 8', value: 'soilTemperature8' },
    ];
  }

  protected getLimits(property: string): any[] {
    const limits = [];

    if (property.startsWith('soilTemperature')) {
      const propertiesToCheck = [
        'soilTemperature', 'soilTemperature1', 'soilTemperature2',
        'soilTemperature3', 'soilTemperature4', 'soilTemperature5',
        'soilTemperature6', 'soilTemperature7', 'soilTemperature8'
      ];

      for (const prop of propertiesToCheck) {
        const foundLimits = super.getLimits(prop);
        limits.push(...foundLimits);
      }
    } else {
      return super.getLimits(property);
    }
    return limits;
  }

  protected getRanges(property: string): any[] {
    const ranges = [];

    if (property.startsWith('soilTemperature')) {
      const propertiesToCheck = [
        'soilTemperature', 'soilTemperature1', 'soilTemperature2',
        'soilTemperature3', 'soilTemperature4', 'soilTemperature5',
        'soilTemperature6', 'soilTemperature7', 'soilTemperature8'
      ];

      for (const prop of propertiesToCheck) {
        const foundRanges = super.getRanges(prop);
        ranges.push(...foundRanges);
      }
      return ranges;
    } else {
      return super.getRanges(property);
    }
  }

  public hasAnyProbeData(sensor: any[]): boolean {
    return sensor.some(probe => probe?.length > 0);
  }

}
